import { useTranslation } from "@hireroo/i18n";
import { useTranslationWithVariable } from "@hireroo/i18n";
import { Notebook, NotebookProps } from "@hireroo/jupyter-ui/react/Notebook";
import { NotebookViewer, NotebookViewerProps } from "@hireroo/jupyter-ui/react/NotebookViewer";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import React from "react";
import { useResizeDetector } from "react-resize-detector";

import IdlePanel, { IdlePanelProps } from "../../modules/IdlePanel/IdlePanel";
import CodeEditor, { CodeEditorProps } from "./parts/CodeEditor/CodeEditor";

const StyledMessageBox = styled(Box)(() => ({
  width: "100%",
  height: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
}));

type MonacoEditor = {
  mode: "MONACO";
  codeEditor: CodeEditorProps;
};

type InjectComponent = {
  mode: "INJECTION";
  children: React.ReactNode;
};

type WaitingConnectServerComponent = {
  mode: "WAITING_CONNECT_SERVER";
};

type JupyterNotebook = {
  mode: "JUPYTER_NOTEBOOK";
  notebook: NotebookProps;
};

type JupyterNotebookViewer = {
  mode: "JUPYTER_NOTEBOOK_VIEWER";
  notebookViewer: NotebookViewerProps;
};

type ImageViewer = {
  mode: "IMAGE";
  src: string;
};

type IdleViewer = {
  mode: "IDLE";
  idlePanel: IdlePanelProps;
};

export type ProjectWorkspaceProps =
  | MonacoEditor
  | JupyterNotebook
  | JupyterNotebookViewer
  | IdleViewer
  | InjectComponent
  | WaitingConnectServerComponent
  | ImageViewer;

const ProjectWorkspace: React.FC<ProjectWorkspaceProps> = props => {
  const isObserve = props.mode === "MONACO";
  const { t } = useTranslation();
  const { t: t2 } = useTranslationWithVariable();
  const resizeDetector = useResizeDetector({
    handleHeight: isObserve,
    handleWidth: isObserve,
  });

  return (
    <Box width={"100%"} height={"100%"} ref={resizeDetector.ref} zIndex={2000}>
      {props.mode === "IDLE" && <IdlePanel {...props.idlePanel} />}
      <Box sx={{ display: props.mode !== "IDLE" ? "block" : "none" }} height={"100%"}>
        {props.mode === "INJECTION" && props.children}
        {props.mode === "MONACO" && props.codeEditor && (
          <CodeEditor {...props.codeEditor} height={resizeDetector.height} width={resizeDetector.width} />
        )}
        {props.mode === "JUPYTER_NOTEBOOK_VIEWER" && props.notebookViewer && <NotebookViewer {...props.notebookViewer} />}
        {props.mode === "JUPYTER_NOTEBOOK" && <Notebook {...props.notebook} />}
        {props.mode === "IMAGE" && (
          <StyledMessageBox>
            <Stack spacing={2} direction="row">
              <Typography variant="body2">
                {t2("CannotDisplayedImage", {
                  src: props.src,
                })}
              </Typography>
            </Stack>
          </StyledMessageBox>
        )}
        {props.mode === "WAITING_CONNECT_SERVER" && (
          <StyledMessageBox>
            <Stack spacing={2} direction="row" alignItems={"center"} justifyContent={"center"}>
              <CircularProgress color="secondary" size="24px" />
              <Typography variant="body2">{t("サーバーとの接続を確立中です。しばらくお待ち下さい。")}</Typography>
            </Stack>
          </StyledMessageBox>
        )}
      </Box>
    </Box>
  );
};

ProjectWorkspace.displayName = "ProjectWorkspace";

export default ProjectWorkspace;
